import { useSFDCAuthState } from "@client-shared/providers/auth";
import useVendorContext, {
  setCurrentLocale,
  setVendor,
} from "@client-shared/providers/vendor";
import * as api from "@client-shared/services/api";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { setAuthLogoutTestId } from "./AuthLogout.utils";
import Redirect from "./Redirect";

export const LOGOUT_TIMEOUT = 1000 * 5;

export const AuthLogout: React.FC = () => {
  const [searchParams] = useSearchParams();
  const logoutType = React.useMemo(
    () => searchParams.get("logout-type"),
    [searchParams],
  );
  const logoutErrorMessage = React.useMemo(
    () => searchParams.get("logout-message"),
    [searchParams],
  );
  const logoutErrorTitle = React.useMemo(
    () => searchParams.get("logout-title"),
    [searchParams],
  );
  const [logoutUrl, setLogoutUrl] = React.useState<string>();
  const [, authDispatch, authUtils] = useSFDCAuthState();
  const [, vendorDispatch] = useVendorContext();
  const logoutTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const { t } = useTranslation();

  React.useEffect(() => {
    authUtils?.clearAuthProvider();
    vendorDispatch(setVendor(undefined));
    vendorDispatch(setCurrentLocale(undefined));
  }, [authDispatch, authUtils, vendorDispatch]);

  const handleFetchLogoutUrl = React.useCallback(async () => {
    const url = await api.getLogoutUrl();
    setLogoutUrl(url);
  }, [setLogoutUrl]);

  React.useEffect(() => {
    if (logoutType === "error") {
      console.error(logoutErrorTitle, logoutErrorMessage);
    }
  }, [logoutType, logoutErrorMessage, logoutErrorTitle]);

  React.useEffect(() => {
    logoutTimeout.current = setTimeout(() => {
      handleFetchLogoutUrl();
    }, LOGOUT_TIMEOUT);

    // Clean up timeout
    return () => {
      if (logoutTimeout.current) {
        clearTimeout(logoutTimeout.current);
        logoutTimeout.current = null;
      }
    };
  }, [handleFetchLogoutUrl]);

  return (
    <>
      {logoutUrl ? (
        <Redirect to={logoutUrl} />
      ) : (
        <div style={{ textAlign: "center", marginTop: "50px" }}>
          {logoutType === "error" ? (
            <>
              <h1 data-testid={setAuthLogoutTestId("error-title")}>
                {t("components.AuthLogout.errorTitle")}
              </h1>
              <p data-testid={setAuthLogoutTestId("error-message")}>
                {t("components.AuthLogout.errorMessage")}
              </p>
            </>
          ) : (
            <>
              <h1 data-testid={setAuthLogoutTestId("title")}>
                {t("components.AuthLogout.title")}
              </h1>
              <p data-testid={setAuthLogoutTestId("message")}>
                {t("components.AuthLogout.message")}
              </p>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default AuthLogout;
